import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { Editor } from '@tiptap/react';
import React, {
  Fragment,
  KeyboardEvent,
  memo,
  MouseEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { formatTiptapAuthorTag } from 'screens/editorial/asset/components/common/Authors';
import {
  getISDDialogStyle,
  getDialogContentStyle,
  inputContainerStyle,
} from './InputSelectDialogStyles';
import { useTranslation } from 'react-i18next';

interface InputDialogProps {
  inputComponent: React.ReactElement;
  contentComponent: React.ReactElement;
  title: string;
  editor?: Editor;
  inputContainerSx?: SxProps<Theme>;
  disabled?: boolean;
  onDialogOpen?: (event?: React.SyntheticEvent) => void;
  onDialogClose?: (event?: React.SyntheticEvent) => void;
  onConfirm?: (event?: React.SyntheticEvent) => void;
}

export const InputSelectDialog = memo(
  ({
    inputComponent,
    contentComponent,
    title,
    editor,
    inputContainerSx,
    disabled,
    onDialogOpen,
    onDialogClose,
    onConfirm,
  }: InputDialogProps) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [isDialogOpen, setDialogOpen] = useState(false);
    const [selections, setSelections] = useState<any[]>([]); // provided by contentComponent interaction
    const [clearSelectionsFlag, setClearSelectionsFlag] = useState(false);
    const selectedCountStr = selections?.length ? `(${selections.length})` : '';
    const { t } = useTranslation();

    const handleDialogOpen = useCallback(
      (event?: React.SyntheticEvent) => {
        if (onDialogOpen) {
          onDialogOpen(event);
        }
        setDialogOpen(true);
      },
      [onDialogOpen],
    );

    const handleDialogClose = useCallback(
      (event?: React.SyntheticEvent) => {
        if (onDialogClose) {
          onDialogClose(event);
        }
        setDialogOpen(false);
      },
      [onDialogClose],
    );

    const handleConfirm = useCallback(
      (event?: React.SyntheticEvent) => {
        if (title === 'Authors' && editor) {
          if (editor?.commands?.setContent) {
            // selections: {desc id label name path} LSSGenericItem
            const formattedTags = selections.map(formatTiptapAuthorTag).join(''); // Generate new <p data...> tags from list
            const newContent = formattedTags.length ? formattedTags : null; // Determine new content for editor
            editor?.commands?.setContent(newContent); // Set the content in the editor
          }
          if (onConfirm) {
            onConfirm(event);
          }
          handleDialogClose(event);
          return;
        }

        if (editor) {
          const displayContent = selections
            .map((option: any) => {
              if (typeof option === 'object' && option?.name) {
                return `<p>${String(option?.name)}</p>`;
              } else {
                return `<p>${String(option)}</p>`;
              }
            })
            .join('');
          editor?.commands?.setContent(displayContent);
        } else if (inputComponent.props.onChange) {
          // Populate a non editor input with selections
          const selectedOptions = selections.join(', ');
          inputComponent.props.onChange({ target: { value: selectedOptions } });
        } else {
          console.warn(
            'No editor or valid input component provided to InputSelectDialog!',
          );
        }

        if (onConfirm) {
          onConfirm(event);
        }

        handleDialogClose(event);
      },
      [editor, title, selections, inputComponent.props, onConfirm, handleDialogClose],
    );

    const handleMouseClick = useCallback(
      (e: MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        if (disabled) {
          return;
        }
        if (!isDialogOpen) {
          handleDialogOpen(e);
        }
      },
      [disabled, handleDialogOpen, isDialogOpen],
    );

    const handleKeyPress = useCallback(
      (e: KeyboardEvent<HTMLDivElement>) => {
        if (disabled) {
          return;
        }
        if (e.key === 'Enter' && !isDialogOpen) {
          handleDialogOpen(e);
        }
      },
      [isDialogOpen, handleDialogOpen, disabled],
    );

    const clearSelectionsInChild = useCallback(() => {
      setClearSelectionsFlag(true);
    }, []);

    // Reset clear selections flag when it is set
    useEffect(() => {
      if (clearSelectionsFlag) {
        setClearSelectionsFlag(false);
      }
    }, [clearSelectionsFlag]);

    return (
      <Fragment>
        <Box
          id="input-select-dialog-box"
          sx={{
            ...inputContainerStyle(theme, Boolean(disabled)),
            ...(inputContainerSx
              ? typeof inputContainerSx === 'function'
                ? inputContainerSx(theme)
                : inputContainerSx
              : {}),
          }}
          tabIndex={0}
          onClick={handleMouseClick}
          onKeyDown={handleKeyPress}
        >
          {/* Render the input target */}
          {inputComponent}
        </Box>

        {!disabled && (
          <Dialog
            fullWidth
            fullScreen={fullScreen}
            open={isDialogOpen}
            sx={getISDDialogStyle}
            onClose={(muiEvent, muiReason) => {
              handleDialogClose(muiEvent as React.SyntheticEvent);
            }}
            transitionDuration={200}
            disableEnforceFocus={true}
          >
            <DialogTitle>{title}</DialogTitle>

            <DialogContent sx={getDialogContentStyle}>
              {/* Render the content component with props passed through */}
              {React.cloneElement(contentComponent, {
                isFocused: isDialogOpen,
                setSelections,
                clearSelectionsFlag,
              })}
            </DialogContent>
            <DialogActions>
              <Button
                startIcon={<DeleteIcon />}
                onClick={clearSelectionsInChild}
                disableElevation
              >
                {t('blox.actions.clear_all')} {selectedCountStr}
              </Button>
              <Box sx={{ flexGrow: 1 }} />
              <Button
                autoFocus
                variant="contained"
                onClick={handleConfirm}
                disableElevation
              >
                {t('blox.actions.update')}
              </Button>
              <Button
                variant="outlined"
                onClick={handleDialogClose}
                autoFocus
                disableElevation
              >
                {t('blox.actions.cancel')}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Fragment>
    );
  },
);

InputSelectDialog.displayName = 'InputSelectDialog';
